import React, { useState, useCallback } from "react";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import {
  ErrorType,
  ModalType,
  usePatientInterest,
} from "context/patient-interest";
import { InterestLevel } from "context/advert/types";
import { EmailForm } from "./email-form";
import { AvailabilityForm } from "./availability-form";
import { CalendarForm } from "./calendar-form-legacy";
import { Confirmation } from "./confirmation";
import { useRiskProfile } from "context/risk-profile";
import type { AvailabilityFormData } from "./availability-form";
import { buildPayloads } from "pages/take-action/payload-helper";
import { useFeatureFlags } from "context/admin-content";

// eslint-disable-next-line react-refresh/only-export-components
export enum FormSection {
  EMAIL = "EMAIL",
  CALENDAR = "CALENDAR",
  AVAILABILITY = "AVAILABILITY",
  CONFIRMATION = "CONFIRMATION",
}

type Props = {
  onClose: () => void;
  isRemote: boolean;
};

export function ScheduleModal({ onClose, isRemote }: Props) {
  const riskProfile = useRiskProfile();
  const featureFlags = useFeatureFlags();
  const patientRiskResult = riskProfile?.patientRiskResult;
  const kioskRiskResult = riskProfile?.kioskRiskResult;
  const isAuth0Session = riskProfile?.isAuth0Session;
  const { setModalType, setErrorType, setInterestLevel } =
    usePatientInterest();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [enteredEmail, setEnteredEmail] = useState<string | undefined>();
  const [availability, setAvailability] = useState<
    AvailabilityFormData | undefined
  >();
  const [dates, setDates] = useState<Date[]>([]);

  let results: any;
  if (isAuth0Session) {
    results = patientRiskResult;
  } else {
    results = kioskRiskResult;
  }

  const useCalendarForm = !featureFlags.removeAccessToNavigatorApp;

  const [formSection, setFormSection] = useState<FormSection>(() => {
    if (isRemote) {
      return useCalendarForm ? FormSection.CALENDAR : FormSection.AVAILABILITY;
    }

    return FormSection.EMAIL;
  });

  const handleConfirm = useCallback(async () => {
    if (useCalendarForm) {
      if (!dates.length) {
        return;
      }

      setSubmitting(true);
      const email = localStorage.getItem("ciq_email") || enteredEmail || "";
      const currentTime = Math.floor(new Date().getTime());
      const timeSlots = dates.map((date) => {
        const startTime = date.getTime();
        const stopTime = startTime + 3600000;

        return {
          start_time: startTime,
          stop_time: stopTime,
        };
      });

      const payload = {
        person: {
          preferred_appointment_times_attributes: timeSlots,
          person_statuses_attributes: [
            {
              status: 21,
            },
          ],
          person_contact_histories_attributes: [
            {
              actionDateTime: currentTime,
              actionType: 17,
              in_person_reason: 1,
              user_email: email,
            },
          ],
        },
      };

      try {
        const idToken = localStorage.getItem("id_token");
        const kioskPatientId = localStorage.getItem(
          "ciq-questionnaire.sessionId"
        );
        const remotePatientId = localStorage.getItem("ciq_id");
        const patientId = isRemote ? remotePatientId : kioskPatientId;

        if (!patientId) {
          setSubmitting(false);
          setErrorType(ErrorType.GENERAL);
          return;
        }

        const response = await fetch(
          `${NAVIGATOR_HOSTNAME}/api/v1/people/${patientId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          console.error("Failed to submit form");
          setSubmitting(false);
          setErrorType(ErrorType.GENERAL);
          return;
        }

        setSubmitting(false);
        if (results?.lockPatientHub && results?.showPatientHub) {
          setModalType(ModalType.LOCK_PATIENT_HUB);
          return;
        }
        if (results?.showPatientHub) {
          window.location.href = "/institution/patient-hub";
        }
        setInterestLevel(InterestLevel.INTERESTED);
        setModalType(ModalType.THANK_YOU);
      } catch (error) {
        setSubmitting(false);
        console.error("Error submitting form: ", error);
        setErrorType(ErrorType.GENERAL);
      }

      return;
    }

    if (!availability) {
      return;
    }

    setSubmitting(true);
    const email = localStorage.getItem("ciq_email") || enteredEmail || "";
    const currentTime = Math.floor(new Date().getTime());
    const { navigatorPayload, specialistPayload } = buildPayloads({
      email,
      currentTime,
      screenings: [],
      preferredAppointmentAvailability: availability,
      specialistCompatiblePayload: !!featureFlags.removeAccessToNavigatorApp,
      kioskOnly: true,
    });

    try {
      const idToken = localStorage.getItem("id_token");
      const kioskPatientId = localStorage.getItem("ciq-questionnaire.sessionId");
      const remotePatientId = localStorage.getItem("ciq_id");
      const patientId = isRemote ? remotePatientId : kioskPatientId;

      if (!patientId) {
        setSubmitting(false);
        setErrorType(ErrorType.GENERAL);
        return;
      }

      const fetchPromises: Promise<Response>[] = [];

      if (navigatorPayload) {
        fetchPromises.push(
          fetch(`${NAVIGATOR_HOSTNAME}/api/v1/people/${patientId}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify(navigatorPayload),
          })
        );
      }

      if (specialistPayload) {
        fetchPromises.push(
          fetch(`${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify(specialistPayload),
          })
        );
      }

      if (fetchPromises.length === 0) {
        setSubmitting(false);
        return;
      }

      const responses = await Promise.all(fetchPromises);

      if (responses.some((r) => !r.ok)) {
        console.error("Failed to submit form");
        setSubmitting(false);
        setErrorType(ErrorType.GENERAL);
        return;
      }
      setSubmitting(false);
      if (results?.lockPatientHub && results?.showPatientHub) {
        setModalType(ModalType.LOCK_PATIENT_HUB);
        return;
      }
      if (results?.showPatientHub) {
        window.location.href = "/institution/patient-hub";
      }
      setInterestLevel(InterestLevel.INTERESTED);
      setModalType(ModalType.THANK_YOU);
    } catch (error) {
      setSubmitting(false);
      console.error("Error submitting form: ", error);
      setErrorType(ErrorType.GENERAL);
    }
  }, [
    availability,
    dates,
    enteredEmail,
    featureFlags.removeAccessToNavigatorApp,
    isRemote,
    results?.lockPatientHub,
    results?.showPatientHub,
    setErrorType,
    setInterestLevel,
    setModalType,
    useCalendarForm,
  ]);

  const handleContinue = useCallback(
    (formData: unknown, completedSection: FormSection) => {
      if (completedSection === FormSection.EMAIL) {
        if (formData && typeof formData === "object" && "email" in formData) {
          setEnteredEmail((formData as { email?: string }).email);
        }
        setFormSection(
          useCalendarForm ? FormSection.CALENDAR : FormSection.AVAILABILITY
        );
      } else if (completedSection === FormSection.CALENDAR) {
        if (
          formData &&
          typeof formData === "object" &&
          "dates" in formData
        ) {
          setDates((formData as { dates?: Date[] }).dates || []);
        }
        setModalType(ModalType.CONFIRMATION);
        setFormSection(FormSection.CONFIRMATION);
      } else if (completedSection === FormSection.AVAILABILITY) {
        if (
          formData &&
          typeof formData === "object" &&
          "availability" in formData
        ) {
          setAvailability(
            (formData as { availability?: AvailabilityFormData }).availability
          );
        }
        setModalType(ModalType.CONFIRMATION);
        setFormSection(FormSection.CONFIRMATION);
      } else if (completedSection === FormSection.CONFIRMATION) {
        handleConfirm();
        return;
      }
    },
    [handleConfirm, setModalType, useCalendarForm]
  );

  const handleEdit = useCallback(() => {
    setErrorType(null);
    setModalType(ModalType.CALENDAR);
    setFormSection(
      useCalendarForm ? FormSection.CALENDAR : FormSection.AVAILABILITY
    );
  }, [setErrorType, setModalType, useCalendarForm]);

  return (
    <>
      {formSection === FormSection.EMAIL && (
        <EmailForm
          onClose={onClose}
          onContinue={handleContinue}
          isSubmitting={isSubmitting}
        />
      )}
      {formSection === FormSection.CALENDAR && (
        <CalendarForm
          dates={dates}
          onClose={onClose}
          onContinue={handleContinue}
          isSubmitting={isSubmitting}
        />
      )}
      {formSection === FormSection.AVAILABILITY && (
        <AvailabilityForm
          availability={availability}
          onClose={onClose}
          onContinue={handleContinue}
          isSubmitting={isSubmitting}
        />
      )}
      {formSection === FormSection.CONFIRMATION && (
        <Confirmation
          availability={availability}
          dates={dates}
          onClose={onClose}
          onEdit={handleEdit}
          onContinue={handleContinue}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}
